import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useStateMachine } from "little-state-machine";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";

import { postInvoiceAuditLogMessage } from "api";
import { invoiceQueryKeys } from "api/queries/invoice/invoiceQueryKeys";
import Button from "components/atoms/Button/Button";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import Textarea from "components/atoms/Textarea/Textarea";
import CardNew from "components/molecules/Card/CardNew";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";
import usePermissions from "hooks/usePermissions";
import { updateBackgroundLoadingState } from "store/store";
import AuditConfirmationDialog from "./AuditConfirmationDialog";
import PaymentStatus from "../PaymentStatus/PaymentStatus";
import Navigation from "../Navigation/Navigation";

const Audit = function ({ isLoading, currentInvoice, setCurrentInvoice, invoices, getAuditLog, shipmentId }) {
  const { permissions } = usePermissions();
  const { actions } = useStateMachine({ updateBackgroundLoadingState });
  const { payment_status_enabled } = JSON.parse(permissions).company_shipper_settings ?? {};
  const intl = useIntl();
  const methods = useForm();
  const { setValue, watch } = methods;
  const { handleSubmit, reset } = methods;
  const queryClient = useQueryClient();

  const action = watch("action");

  const invoiceStatus = currentInvoice?.invoice_status;
  const invoicePaymentStatus = currentInvoice?.payment_status;
  const invoiceConfirmed = invoiceStatus === "Approved" || invoiceStatus === "Declined";
  const invoiceAutoRemoved = invoiceStatus === "Auto Removed";

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [commentData, setCommentData] = useState({});

  const dropdownValues = [
    {
      value: "awaiting_audit",
      label: intl.formatMessage({
        id: "billing.awaitingAudit",
        defaultMessage: "Awaiting Audit",
      }),
    },
    {
      value: "approved",
      label: intl.formatMessage({
        id: "general.approved",
        defaultMessage: "Approved",
      }),
    },
    {
      value: "declined",
      label: intl.formatMessage({
        id: "shipment-quotes.components.invitedProviders.declined",
        defaultMessage: "Declined",
      }),
    },
  ];

  useEffect(() => {
    reset();
  }, [currentInvoice]);

  const postComment = (data) => {
    const payload = {
      comment: data.comment_message,
      action: data.action,
    };
    postInvoiceAuditLogMessage(currentInvoice.invoice_id, payload)
      .then(() => {
        reset();
        closeConfirmationDialog();
        actions.updateBackgroundLoadingState(true);
        queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.shipmentInvoicesListing(shipmentId) });
        getAuditLog();
        toast("Audit status updated successfully", { type: "success" });
      })
      .catch((err) => errorToast(err));
  };

  const handleConfirmationDialog = (data) => {
    setCommentData(data);
    if (data.action === "awaiting_audit") postComment(data);
    else setShowConfirmationDialog(true);
  };

  const closeConfirmationDialog = () => setShowConfirmationDialog(false);

  return (
    <>
      <AuditConfirmationDialog
        isOpen={showConfirmationDialog}
        close={closeConfirmationDialog}
        data={commentData}
        submitComment={postComment}
      />
      <CardNew className="mb-15" isLoading={isLoading}>
        <h5 style={{ margin: "0px 0 16px 0" }}>
          <FormattedMessage id="general.invoices" defaultMessage="Invoices" />
        </h5>
        <Navigation invoices={invoices} currentInvoice={currentInvoice} setCurrentInvoice={setCurrentInvoice} />
        {invoiceStatus === "Approved" && payment_status_enabled && (
          <PaymentStatus invoiceId={currentInvoice?.invoice_id} paymentStatus={invoicePaymentStatus} />
        )}
        {permissions &&
          JSON.parse(permissions).permissions.invoicing.audit_invoices &&
          !invoiceConfirmed &&
          !invoiceAutoRemoved && (
            <FormProvider {...methods}>
              {currentInvoice.invoice_status !== "Removed" && (
                <form onSubmit={handleSubmit(handleConfirmationDialog)}>
                  <p className="mb-1 mt-1 font-b3">
                    <FormattedMessage id="billing.changingStatusOf" defaultMessage="Changing status of" />{" "}
                    {currentInvoice?.invoice_id}
                  </p>
                  <Dropdown
                    key={currentInvoice?.invoice_id}
                    options={dropdownValues}
                    onChange={(item) => {
                      setValue("action", item.value);
                    }}
                    defaultValue={currentInvoice?.invoice_status?.replace(" ", "_").toLowerCase()}
                  />

                  {["approved", "declined"].includes(action) && (
                    <>
                      <Textarea
                        style={{
                          borderRadius: "12px",
                          borderColor: "black",
                          resize: "none",
                          height: "auto",
                          marginTop: 8,
                          marginBottom: 8,
                        }}
                        name="comment_message"
                        placeholder={intl.formatMessage({
                          id: "billing.newCommentPlaceholder",
                          defaultMessage: "Your optional comment...",
                        })}
                        rows={3}
                      />
                      <Button
                        className="ml-auto"
                        label={intl.formatMessage({
                          id: "general.submit",
                          defaultMessage: "Submit",
                        })}
                        variant="primary"
                        type="submit"
                        data-testid={testIds.SHIPMENT_DASHBOARD_AUDIT_STATUS_SUBMIT_BUTTON}
                      />
                    </>
                  )}
                </form>
              )}
            </FormProvider>
          )}
      </CardNew>
    </>
  );
};

Audit.propTypes = {
  invoices: PropTypes.instanceOf(Array).isRequired,
  isLoading: PropTypes.bool.isRequired,
  currentInvoice: PropTypes.instanceOf(Object).isRequired,
  setCurrentInvoice: PropTypes.func.isRequired,
  shipmentId: PropTypes.string.isRequired,
  getAuditLog: PropTypes.func.isRequired,
};

export default Audit;
